import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Link,
    Paper,
    Typography,
    Grid,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    TextField,
    Button,
    DialogActions,
    FormGroup,
    Divider,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useAuth } from "../../helpers/AuthProvider";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
import MiniBot from "../MiniBot";
import queryGraphql from "../../helpers/ApiInterface"; // Added API helper import
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import mermaid from "mermaid"; // Mermaid library for diagrams

// --- NEW: Swagger UI and YAML Parser Imports ---
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css"; // Essential CSS for Swagger UI
import yaml from "js-yaml";
import PageviewIcon from '@mui/icons-material/Pageview'; // Icon for the new button


// --- Define paths for custom SVG icons ---
const API_CATALOGUE_LOGO_PATH = "/assets/aws-cloud-monitoring.svg";

// --- Accordion Arrow Icons ---
const ARROW_RIGHT_ICON_PATH = "/assets/arrow-right-s-line.svg";
const ARROW_DOWN_ICON_PATH = "/assets/arrow-down.svg";
const ICON_CONTAINER_BG_COLOR = '#F2F4F7';

// --- NEW: Hardcoded Swagger/OpenAPI YAML for demonstration ---
// In a real application, this would come from the `api.service.swaggerTemplate` field.
const hardcodedSwaggerYaml = `openapi: 3.0.3

# 1. Info: High-level information about your API.
# -----------------------------------------------------
info:
  title: "Your Awesome API Title"
  description: |
    This is a sample API server. 
    You can find out more about this project at [https://example.com](https://example.com).
    This description supports markdown.
  termsOfService: "http://example.com/terms/"
  contact:
    name: "API Support Team"
    url: "http://www.example.com/support"
    email: "support@example.com"
  license:
    name: "Apache 2.0"
    url: "https://www.apache.org/licenses/LICENSE-2.0.html"
  version: "1.0.0"

# 2. Servers: Define one or more base URLs for your API.
# Variables allow for dynamic switching between environments in Swagger UI.
# -------------------------------------------------------------------------
servers:
  - url: "https://{environment}.example.com/api/v1"
    description: "Dynamic server URL"
    variables:
      environment:
        default: "development"
        description: "The deployment environment."
        enum:
          - "development"
          - "staging"
          - "production"
  - url: "http://localhost:3000/api/v1"
    description: "Local development server"

# 3. Tags: Used for grouping related endpoints in the UI.
# --------------------------------------------------------
tags:
  - name: "Users"
    description: "Operations about users"
  - name: "Products"
    description: "Access to product information"
  - name: "Health"
    description: "API Health Check"

# 4. Paths: Define your API endpoints.
# This section references reusable components defined below.
# --------------------------------------------------------
paths:
  # Health Check Endpoint (publicly accessible)
  /health:
    get:
      tags:
        - "Health"
      summary: "Check API Health"
      description: "Returns the operational status of the API."
      operationId: "getHealth"
      security: [] # Override global security to make this endpoint public
      responses:
        '200':
          description: "API is healthy"
          content:
            application/json:
              schema:
                type: "object"
                properties:
                  status:
                    type: "string"
                    example: "OK"
        '503':
          $ref: '#/components/responses/ServiceUnavailable'

  # Path for a collection of resources (e.g., /users)
  /users:
    get:
      tags:
        - "Users"
      summary: "List all users"
      description: "Gets a paginated list of all users in the system."
      operationId: "getUsers"
      parameters:
        - $ref: '#/components/parameters/LimitParam'
        - $ref: '#/components/parameters/OffsetParam'
      responses:
        '200':
          description: "A successful response with a list of users."
          content:
            application/json:
              schema:
                type: "array"
                items:
                  $ref: '#/components/schemas/User'
        '400':
          $ref: '#/components/responses/BadRequest'
        '401':
          $ref: '#/components/responses/Unauthorized'

    post:
      tags:
        - "Users"
      summary: "Create a new user"
      description: "Adds a new user to the system."
      operationId: "createUser"
      requestBody:
        $ref: '#/components/requestBodies/UserBody'
      responses:
        '201':
          description: "User created successfully."
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '400':
          $ref: '#/components/responses/BadRequest'
        '401':
          $ref: '#/components/responses/Unauthorized'

  # Path for a specific resource (e.g., /users/{userId})
  /users/{userId}:
    get:
      tags:
        - "Users"
      summary: "Find user by ID"
      description: "Returns a single user based on their ID."
      operationId: "getUserById"
      parameters:
        - $ref: '#/components/parameters/UserId'
      responses:
        '200':
          description: "Successful operation"
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '401':
          $ref: '#/components/responses/Unauthorized'
        '404':
          $ref: '#/components/responses/NotFound'

    put:
      tags:
        - "Users"
      summary: "Update an existing user"
      description: "Updates the information for a specified user."
      operationId: "updateUser"
      parameters:
        - $ref: '#/components/parameters/UserId'
      requestBody:
        $ref: '#/components/requestBodies/UserBody'
      responses:
        '200':
          description: "User updated successfully."
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/User'
        '400':
          $ref: '#/components/responses/BadRequest'
        '401':
          $ref: '#/components/responses/Unauthorized'
        '404':
          $ref: '#/components/responses/NotFound'

    delete:
      tags:
        - "Users"
      summary: "Delete a user"
      description: "Deletes a user by their ID."
      operationId: "deleteUser"
      parameters:
        - $ref: '#/components/parameters/UserId'
      responses:
        '204':
          description: "No Content. User deleted successfully."
        '401':
          $ref: '#/components/responses/Unauthorized'
        '404':
          $ref: '#/components/responses/NotFound'


# 5. Components: Reusable elements for your API specification.
# This is the core of a dynamic and maintainable OpenAPI document.
# ---------------------------------------------------------------
components:
  # Reusable Schemas (Data Models)
  schemas:
    User:
      type: "object"
      description: "Represents a user in the system."
      required:
        - id
        - email
        - name
      properties:
        id:
          type: "string"
          format: "uuid"
          description: "The unique identifier for the user."
          example: "d290f1ee-6c54-4b01-90e6-d701748f0851"
        email:
          type: "string"
          format: "email"
          description: "User's email address."
          example: "john.doe@example.com"
        name:
          type: "object"
          properties:
            first:
              type: "string"
              example: "John"
            last:
              type: "string"
              example: "Doe"
        status:
          type: "string"
          enum: [ "active", "inactive", "pending" ]
          default: "pending"
        createdAt:
          type: "string"
          format: "date-time"
          readOnly: true
        updatedAt:
          type: "string"
          format: "date-time"
          readOnly: true

    Error:
      type: "object"
      properties:
        code:
          type: "integer"
          format: "int32"
        message:
          type: "string"
      required:
        - code
        - message

  # Reusable Parameters
  parameters:
    UserId:
      name: "userId"
      in: "path"
      description: "ID of the user to perform an operation on."
      required: true
      schema:
        type: "string"
        format: "uuid"
    LimitParam:
      name: "limit"
      in: "query"
      description: "The number of items to return."
      required: false
      schema:
        type: "integer"
        format: "int32"
        minimum: 1
        maximum: 100
        default: 20
    OffsetParam:
      name: "offset"
      in: "query"
      description: "The number of items to skip for pagination."
      required: false
      schema:
        type: "integer"
        format: "int32"
        minimum: 0
        default: 0

  # Reusable Request Bodies
  requestBodies:
    UserBody:
      description: "A JSON object containing user information."
      required: true
      content:
        application/json:
          schema:
            type: "object"
            properties:
              email:
                type: "string"
                format: "email"
              name:
                type: "object"
                properties:
                  first:
                    type: "string"
                  last:
                    type: "string"
            required:
              - email
              - name


  # Reusable Responses
  responses:
    NotFound:
      description: "The specified resource was not found."
      content:
        application/json:
          schema:
            $ref: '#/components/schemas/Error'
    BadRequest:
      description: "Bad request. The request payload is invalid."
      content:
        application/json:
          schema:
            $ref: '#/components/schemas/Error'
    Unauthorized:
      description: "Unauthorized. API key is missing or invalid."
      content:
        application/json:
          schema:
            $ref: '#/components/schemas/Error'
    ServiceUnavailable:
      description: "Service Unavailable. The server is currently unable to handle the request."
      content:
        application/json:
          schema:
            $ref: '#/components/schemas/Error'

  # Reusable Security Schemes
  securitySchemes:
    ApiKeyAuth:
      type: "apiKey"
      in: "header"
      name: "X-API-KEY"
    BearerAuth:
      type: "http"
      scheme: "bearer"
      bearerFormat: "JWT"
      description: "Enter your JWT token."

# 6. Security: Define which security schemes are applied globally.
# This can be overridden on a per-operation basis.
# ---------------------------------------------------------------
security:
  - BearerAuth: []
  - ApiKeyAuth: []`;


// --- GraphQL Queries ---
const getIntegrationsQuery = `
  query GetIntegrations {
    getIntegrations {
      customAPICatalogue {
        updatedAt
      }
      testingEnvironment {
        enabled
      }
    }
  }
`;

// --- NEW: Add the mutation for K8s deployment ---
const makeK8sDeploymentsMutation = `
  mutation MakeK8sDeployments {
    makeK8sDeployments
  }
`;

const enableAndDeployTestingEnvMutation = `
  mutation EnableAndDeployTestingEnvironment($input: UpdateIntegrationsInput!) {
    updateIntegration(input: $input) {
      testingEnvironment {
        enabled
      }
    }
  }
`;

const getAPIGatewaysQuery = `
  query GetAPIGateways {
    getAPIGateways {
      name
      id
      isPublished
      status
      apiBackends {
        name
        httpMethod
        mockRequest
        mockResponse
        description
        id
        path
        updatedAt
        codeTemplate
        serviceType
        service {
          swaggerTemplate
          sequenceDiagramTemplate
        }
      }
      apiGatewayCategory
    }
  }
`;

const getAPICredentialsQuery = `
  query GetAPICatalogueCredentials($gatewayName: String!) {
    getAPICatalogueCredentials(gatewayName: $gatewayName)
  }
`;

const getResourceStatusesQuery = `
  query ResourceStatuses {
    resourceStatuses {
      resourceName
      projectUrl
      solutionEndpointUrl
    }
  }
`;
// --- NEW: Add the Clone API Mutation ---
const cloneAPIMutation = `
  mutation CloneAPIBackend($input: CloneAPIBackendInput!) {
    cloneAPIBackend(input: $input) {
      id
      name
    }
  }
`;

// --- Data Structure Interfaces (No changes needed here) ---
interface ApiBackendDetail {
    id: string;
    name: string;
    description: string;
    endpoint: string;
    method: string;
    inputSchema: object;
    outputSchema: object;
    updatedAt: number;
    codeTemplate: string;
    serviceType: string;
    swaggerTemplate?: string; 
}

interface GatewayDetail {
    id: string;
    name: string;
    isPublished: boolean;
    category: string;
    status: string;
    apiBackends: ApiBackendDetail[];
}

interface CredentialDisplayBoxProps {
    label: string;
    value: string;
}

// --- Mermaid Diagram Renderer Component ---
interface MermaidDiagramProps {
    chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
    const mermaidRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const renderDiagram = async () => {
            if (mermaidRef.current && chart && typeof chart === 'string' && chart.trim() !== '') {
                try {
                    const uniqueId = `mermaid-graph-${Math.random().toString(36).substr(2, 9)}`;
                    const { svg } = await mermaid.render(uniqueId, chart);

                    if (mermaidRef.current) {
                        mermaidRef.current.innerHTML = svg;
                    }
                } catch (error) {
                    console.error("Failed to render Mermaid diagram:", error);
                    if (mermaidRef.current) {
                        mermaidRef.current.innerHTML = "Error rendering diagram. Please check the syntax.";
                    }
                }
            } else if (mermaidRef.current) {
                mermaidRef.current.innerHTML = "";
            }
        };

        renderDiagram();
    }, [chart]);

    if (!chart) return null;

    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', overflowX: 'auto', mt: { xs: 1, md: 0 } }}>
            <Box ref={mermaidRef} sx={{
                '& svg': {
                    maxWidth: '100%',
                    height: 'auto',
                }
            }} />
        </Paper>
    );
};


const CredentialDisplayBox: React.FC<CredentialDisplayBoxProps> = ({ label, value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    };

    const { user } = useAuth();

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
                {label}
            </Typography>
            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '8px 12px',
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    mt: 0.5
                }}
            >
                <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {value}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <IconButton onClick={handleCopy} size="small">
                        <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Paper>
        </Box>
    );
};


// --- React Component ---
const CustomAPICatalogueContent: React.FC = () => {
    const navigate = useNavigate();
const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    // --- INTERFACE CHANGE: Added swaggerTemplate to the flattened data structure ---
    interface ApiBackendWithGatewayInfo {
        id: string;
        backendId: string;    // The actual backend ID for the clone mutation
        name: string;
        description: string;
        endpoint: string;
        method: string;
        inputSchema: object;
        outputSchema: object;
        updatedAt: number;
        codeTemplate: string;
        serviceType: string;
        swaggerTemplate?: string; // Optional swagger YAML content
        sequenceDiagram?: string;
        gatewayId: string;
        gatewayName: string;
        gatewayCategory: string;
        isPublished: boolean;
    }


    // --- STATE CHANGE: Updated state to handle a flat list and new Swagger modal ---
    const [apiBackendsData, setApiBackendsData] = useState<ApiBackendWithGatewayInfo[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [listError, setListError] = useState<string | null>(null);
    const [expandedApiId, setExpandedApiId] = useState<string | null>(null);
    const [installationDate, setInstallationDate] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [categories, setCategories] = useState<string[]>(["All"]);
    const [selectedCategory, setSelectedCategory] = useState<string>("All");
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isCredentialsLoading, setIsCredentialsLoading] = useState<boolean>(false);
    const [credentials, setCredentials] = useState<{ clientId: string; clientSecret: string; clientKey: string } | null>(null);
    const [credentialsError, setCredentialsError] = useState<string | null>(null);
    const [resourceUrls, setResourceUrls] = useState<{ [key: string]: string }>({});
    
    const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);
    const [currentDiagram, setCurrentDiagram] = useState<string>("");
    
    // --- NEW: State for Swagger UI Modal ---
    const [isSwaggerModalOpen, setIsSwaggerModalOpen] = useState<boolean>(false);
    const [currentSwaggerYaml, setCurrentSwaggerYaml] = useState<string | null>(null);

    // --- NEW: State for Clone Modal ---
    const [isCloneModalOpen, setIsCloneModalOpen] = useState<boolean>(false);
    const [apiToClone, setApiToClone] = useState<ApiBackendWithGatewayInfo | null>(null);
    const [newApiName, setNewApiName] = useState<string>("");
    const [isCloning, setIsCloning] = useState<boolean>(false);
    const [cloneError, setCloneError] = useState<string | null>(null);

    const [isTestingEnvEnabled, setIsTestingEnvEnabled] = useState<boolean>(false);
    const [isSettingEnv, setIsSettingEnv] = useState<boolean>(false);
    const [setEnvError, setSetEnvError] = useState<string | null>(null);
    const [setEnvSuccess, setSetEnvSuccess] = useState<string | null>(null);

    const handleSetTestingEnvironment = async () => {
    setIsSettingEnv(true);
    setSetEnvError(null);
    setSetEnvSuccess(null);

    try {
        const response = await queryGraphql(makeK8sDeploymentsMutation);

        const variables = {
            "input": {
                "testingEnvironment": {
                    "enabled": true
                }
            }
        };
        
        if (response?.makeK8sDeployments) {
            setSetEnvSuccess("Testing environment setup initiated successfully! The status will update shortly.");
            // Refresh data to get the latest 'enabled' status
            const response = await queryGraphql(enableAndDeployTestingEnvMutation, variables);
            setTimeout(fetchData, 3000); // Optional: add a small delay before refetching
        } else if (response?.errors) {
            throw new Error(response.errors[0]?.message || 'Failed to initiate deployment.');
        } else {
            throw new Error('Received an unexpected response from the server.');
        }
    } catch (err: any) {
        setSetEnvError(err.message || "An unknown error occurred.");
    } finally {
        setIsSettingEnv(false);
    }
};

    const fetchData = async () => {
        setIsLoading(true);
        setListError(null);
        setSetEnvError(null); 
        setSetEnvSuccess(null);
        try {
            const [integrationsResponse, gatewaysResponse, resourceStatusesResponse] = await Promise.all([
                queryGraphql(getIntegrationsQuery),
                queryGraphql(getAPIGatewaysQuery),
                queryGraphql(getResourceStatusesQuery)
            ]);

            if (resourceStatusesResponse?.resourceStatuses) {
                const urlMap = resourceStatusesResponse.resourceStatuses.reduce((acc: any, status: any) => {
                    if (status.resourceName && status.solutionEndpointUrl) {
                        acc[status.resourceName] = status.solutionEndpointUrl;
                    }
                    return acc;
                }, {});
                setResourceUrls(urlMap);
            }

            if (integrationsResponse?.getIntegrations) {
            // --- MODIFIED: Process both customAPICatalogue and testingEnvironment ---
            if (integrationsResponse.getIntegrations.customAPICatalogue?.updatedAt) {
                setInstallationDate(integrationsResponse.getIntegrations.customAPICatalogue.updatedAt);
            }
            // --- NEW: Check the testing environment status ---
            if (integrationsResponse.getIntegrations.testingEnvironment) {
                setIsTestingEnvEnabled(integrationsResponse.getIntegrations.testingEnvironment.enabled || false);
            }
        }

            // --- DATA TRANSFORMATION CHANGE: Flatten the nested structure and include swaggerTemplate ---
            if (gatewaysResponse?.getAPIGateways && Array.isArray(gatewaysResponse.getAPIGateways)) {
                               
                const flattenedApiBackends: ApiBackendWithGatewayInfo[] = gatewaysResponse.getAPIGateways
                    .filter((gateway: any) => gateway.status === 'active')
                    .flatMap((gateway: any) =>
                        (gateway.apiBackends || []).map((apiBackend: any) => {
                            let inputSchema = {};
                            let outputSchema = {};
                            try {
                                if (apiBackend.mockRequest) inputSchema = JSON.parse(apiBackend.mockRequest);
                            } catch (e) {
                                console.error(`Failed to parse inputSchema for API ${apiBackend.name}:`, e);
                            }
                            try {
                                if (apiBackend.mockResponse) outputSchema = JSON.parse(apiBackend.mockResponse);
                            } catch (e) {
                                console.error(`Failed to parse outputSchema for API ${apiBackend.name}:`, e);
                            }

                            return {
                                id: `${gateway.id}-${apiBackend.id}`,
                                backendId: apiBackend.id, // --- ADD THIS LINE ---
                                name: apiBackend.name,
                                description: apiBackend.description || 'No description available.',
                                endpoint: apiBackend.path,
                                method: apiBackend.httpMethod,
                                inputSchema: inputSchema,
                                outputSchema: outputSchema,
                                updatedAt: apiBackend.updatedAt || 0,
                                codeTemplate: apiBackend.codeTemplate || 'N/A',
                                serviceType: apiBackend.serviceType || 'N/A',
                                // --- NEW: Pass swagger template. For now, we also add the hardcoded one as a fallback.
                                swaggerTemplate: apiBackend.service?.swaggerTemplate || hardcodedSwaggerYaml,
                                sequenceDiagram: apiBackend.service?.sequenceDiagramTemplate,
                                gatewayId: gateway.id,
                                gatewayName: gateway.name,
                                isPublished: gateway.isPublished,
                                gatewayCategory: gateway.apiGatewayCategory || 'Uncategorized',
                            };
                        })
                    );

                setApiBackendsData(flattenedApiBackends);

                const uniqueCategories = new Set(flattenedApiBackends.map(api => api.gatewayCategory));
                setCategories(["All", ...Array.from(uniqueCategories)]);

            } else if (gatewaysResponse?.errors) {
                setListError(`GraphQL Error: ${gatewaysResponse.errors[0]?.message || 'Unknown GraphQL error'}`);
                setApiBackendsData([]);
            } else {
                setListError("Could not fetch API Gateway data or the format is incorrect.");
                setApiBackendsData([]);
            }
        } catch (err: any) {
            setListError(err.message || "An unexpected error occurred.");
            setApiBackendsData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        mermaid.initialize({
            startOnLoad: false,
            theme: 'neutral',
            sequence: {
                showSequenceNumbers: true,
            },
        });
        
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApiAccordionChange = (apiId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedApiId(isExpanded ? apiId : null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCredentials(null);
        setCredentialsError(null);
    }; 

    const handleOpenDiagramModal = (diagramCode: string) => {
        setCurrentDiagram(diagramCode);
        setIsDiagramModalOpen(true);
    };

    const handleCloseDiagramModal = () => {
        setIsDiagramModalOpen(false);
        setCurrentDiagram("");
    };

    const handleShowCredentials = async (e: React.MouseEvent, gatewayName: string) => {
    e.stopPropagation();
    setIsModalOpen(true); // Open the modal regardless of role
    setCredentials(null);
    setCredentialsError(null);

    // --- NEW: Check for viewer role right at the start ---
    if (isViewer) {
        setCredentialsError("Viewer role does not grant access to credentials. Please contact an admin.");
        setIsCredentialsLoading(false); // We are not loading anything
        return; // Stop the function here for viewers
    }
    // --- END OF NEW CODE ---

    setIsCredentialsLoading(true); // Set loading for non-viewers

    try {
        const response = await queryGraphql(getAPICredentialsQuery, { gatewayName });
        if (response?.getAPICatalogueCredentials) {
            setCredentials(response.getAPICatalogueCredentials);
        } else if (response?.errors) {
            throw new Error(response.errors[0]?.message || 'Failed to fetch credentials.');
        } else {
            throw new Error('Received an unexpected response format.');
        }
    } catch (err: any) {
        setCredentialsError(err.message || "An unknown error occurred.");
    } finally {
        setIsCredentialsLoading(false);
    }
};

    // --- NEW: Handlers for Swagger UI Modal ---
    const handleOpenSwaggerModal = (yamlContent: string | undefined, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent accordion from closing/opening
        if (yamlContent) {
            setCurrentSwaggerYaml(yamlContent);
            setIsSwaggerModalOpen(true);
        }
    };

    const handleCloseSwaggerModal = () => {
        setIsSwaggerModalOpen(false);
        setCurrentSwaggerYaml(null);
    };

    // --- NEW: Handlers for Clone Modal ---
    const handleOpenCloneModal = (api: ApiBackendWithGatewayInfo, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent accordion from toggling
        setApiToClone(api);
        setNewApiName(`${api.name}-clone`); // Set a default new name
        setIsCloneModalOpen(true);
        setCloneError(null);
    };

    const handleCloseCloneModal = () => {
        setIsCloneModalOpen(false);
        setApiToClone(null);
        setNewApiName("");
        setIsCloning(false);
        setCloneError(null);
    };

    const handleCloneApi = async () => {
        if (!apiToClone || !newApiName.trim()) {
            setCloneError("API name cannot be empty.");
            return;
        }

        setIsCloning(true);
        setCloneError(null);

        try {
            const variables = {
                input: {
                    id: apiToClone.backendId, // Use the correct backend ID
                    newName: newApiName.trim()
                }
            };
            
            const response = await queryGraphql(cloneAPIMutation, variables);

            if (response?.cloneAPIBackend?.id) {
                handleCloseCloneModal();
                fetchData(); // Refresh the list to show the new API
            } else if (response?.errors) {
                throw new Error(response.errors[0]?.message || 'Failed to clone the API.');
            } else {
                throw new Error('Received an unexpected response from the server.');
            }
        } catch (err: any) {
            setCloneError(err.message || "An unknown error occurred.");
        } finally {
            setIsCloning(false);
        }
    };

    // --- NEW: Memoized parsing of YAML content ---
const swaggerSpec = useMemo(() => {
    try {
        if (!currentSwaggerYaml) {
            return null;
        }

        const spec = yaml.load(currentSwaggerYaml);

        // Create a mutable copy of the spec to modify it safely
        const specWithoutSecurity = JSON.parse(JSON.stringify(spec));

        // 1. Remove the global security definition to hide the 'Authorize' button
        delete specWithoutSecurity.security;

        // 2. Remove the security schemes from components
        if (specWithoutSecurity.components) {
            delete specWithoutSecurity.components.securitySchemes;
        }

        return specWithoutSecurity; // Return the modified spec

    } catch (e) {
        console.error("Error parsing YAML spec:", e);
        // Fallback for any YAML parsing errors
        return {
            openapi: '3.0.0',
            info: { title: 'YAML Parsing Error', version: '1.0.0' },
            paths: { '/error': { get: { summary: 'Could not parse the YAML file.', description: String(e) } } }
        };
    }
}, [currentSwaggerYaml]);


    const formatDate = (timestamp: string | number | null | undefined): string => {
        if (!timestamp) return "N/A";
        const numericTimestamp = Number(timestamp);
        if (isNaN(numericTimestamp)) return "Invalid Date";
        const date = new Date(numericTimestamp);
        return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    const filteredApiBackends = useMemo(() => {
        return apiBackendsData
            .filter(api => {
                const categoryMatch = selectedCategory === "All" || api.gatewayCategory === selectedCategory;
                const searchMatch =
                    api.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    api.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    api.gatewayName.toLowerCase().includes(searchQuery.toLowerCase());

                return categoryMatch && searchMatch;
            })
            .sort((a, b) => {
                if (a.isPublished !== b.isPublished) {
                    return Number(b.isPublished) - Number(a.isPublished);
                }
                return a.name.localeCompare(b.name);
            });
    }, [apiBackendsData, searchQuery, selectedCategory]);

    return (
        <>
        <Box bgcolor="#FFFFFF" pt={2} mb="6px">
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: 1,
								mx: 2,
							}}
						>
							<Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
								Custom API Catalogue
							</Typography>
						

							
					<Box sx={{display:'flex', justifyContent:'center'}}>
                          
                            
							
							<IconButton
									  aria-label="Help" 
									  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
									  sx={{
										padding: 0, 
										border: 'none',  
										ml:2,
										'&:hover': {
										  
										  backgroundColor: 'action.hover', 
										},
										
									  }}
									>
									  <Box
										component="img"
										src="/assets/Help.svg" 
										alt="" 
										sx={{
										  width: 40,  
										  height: 40,
										  display: 'block', 
										}}
									  />
									</IconButton>

</Box>

						</Box>
						<Divider sx={{ marginY: 1 }} />
						
					</Box>

                <Box sx={{ display: "flex", marginBottom: 2, justifyContent: "space-between", alignItems: "center" , px:2}} >
                                    <TextField
                                        variant="filled"
                                        placeholder="Search by API or gateway name..."
                                        value={searchQuery}
                                        size="small"
                                        hiddenLabel
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: ( <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "14px", marginRight: "8px" }} /> ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", height: "45px", width: "450px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                </Box>    
            <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
                {/* Header and Breadcrumbs (No changes) */}
                
                    
                {/* <Box sx={{ mb: 3 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
                        <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
                        <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Custom API Catalogue </Typography>
                    </Breadcrumbs>
                    <Box display="flex" justifyContent="space-between">
                        <Box display="flex" alignItems="center" flexDirection="row" >
                            <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
                            <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Custom API Catalogue </Typography>
                        </Box>
                        <IconButton
                            aria-label="Help"
                            onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                            sx={{ padding: 0, border: 'none', ml: 2, '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box component="img" src="/assets/Help.svg" alt="Help" sx={{ width: 40, height: 40, display: 'block' }} />
                        </IconButton>
                    </Box>
                </Box> */}
                
                <Box sx={{ mx: 'auto' }}>
                    {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}> <CircularProgress size={30} /> <Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading API Catalogue Data...</Typography> </Paper> )}
                    {!isLoading && listError && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}> <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error loading list: {listError} </Typography> </Paper> )}

                    {!isLoading && !listError && (
                        <>
                            {/* Search and info header (No changes) */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {/* <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}>
                                    Manage and discover your published APIs.
                                </Typography> */}
                                
                                
                            </Box>
                            {/* <Paper elevation={2} sx={{ borderRadius: "12px", p: 2.5, mb: 3, overflow: 'hidden' }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm="auto"> <Avatar alt="Custom API Catalogue Logo" variant="rounded" sx={{ width: 50, height: 50, bgcolor: "#F9F9F9", p: 0.5 }}> <img src={API_CATALOGUE_LOGO_PATH} alt="" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} /> </Avatar> </Grid>
                                    <Grid item xs={12} sm>
                                        <Box display="flex" alignItems="center" mb={0.5} >
                                            <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Custom API Catalogue </Typography>
                                            <Chip label="API MANAGEMENT" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
                                            
                                        </Box>
                                        
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1 }}> A centralized catalogue for all your APIs. </Typography>
                                    </Grid>
                                    <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1.5, sm: 0 } }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleSetTestingEnvironment}
                                        disabled={isTestingEnvEnabled || isSettingEnv}
                                        sx={{
                                            backgroundColor: "black",
                                            fontFamily: 'Gilroy-Bold',
                                            color: "white",
                                            fontSize: '12px',
                                            borderRadius: '6px',
                                            textTransform: "none",
                                            py: 0.5,
                                            px: 2, // Added some padding for better look
                                            minWidth: '180px', // Ensure button doesn't resize drastically
                                            mr: { xs: 0, md: 2 },
                                            '&:hover': {
                                                backgroundColor: '#333'
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                color: 'rgba(0, 0, 0, 0.26)',
                                            }
                                        }}
                                    >
                                        {isSettingEnv ? <CircularProgress size={20} color="inherit" /> : 'Set Testing Environment'}
                                    </Button>
                                        </Grid>
                                    <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1.5, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Installed On: {formatDate(installationDate)} </Typography> </Grid>
                                </Grid>
                            </Paper> */}
                            
                            {/* Category buttons (No changes) */}
                            

                            <Box>
                                {filteredApiBackends.length === 0 && !isLoading && (
                                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: "1px dashed #E0E0E0", borderRadius: "12px", bgcolor: '#FDFDFD' }}>
                                        <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular' }}> No APIs match your search criteria. </Typography>
                                    </Paper>
                                )}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={1}>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>

                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="1.1rem"> Available APIs ({filteredApiBackends.length}) </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                                {categories.map((category) => (
                                    <Button key={category} onClick={() => setSelectedCategory(category)} variant="outlined" sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 500, fontSize: "12px", padding: "4px 12px", borderWidth: "1px", backgroundColor: category === selectedCategory ? "#F5F5F5" : "transparent", color: "#333", borderColor: category === selectedCategory ? "#000" : "#DADADA", "&:hover": { backgroundColor: "#EAEAEA", borderColor: "#000" }, }}>
                                        {category}
                                    </Button>
                                ))}
                            </Box>
                                </Box>


                                {filteredApiBackends.map((api) => {
                                    const isApiExpanded = expandedApiId === api.id;
                                    return (
                                        <Accordion key={api.id} expanded={isApiExpanded} onChange={handleApiAccordionChange(api.id)} disableGutters elevation={0} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: 'none', '&:before': { display: 'none' }, mb: 1.5, overflow: 'hidden', bgcolor: '#ffffff' }}>
                                            <AccordionSummary aria-controls={`${api.id}-content`} id={`${api.id}-header`} sx={{ px: { xs: 2, md: 2.5 }, py: 0.5, minHeight: '60px', '& .MuiAccordionSummary-content': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1.5 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden', minWidth: 0 }}>
                                                    <Box sx={{ bgcolor: ICON_CONTAINER_BG_COLOR, borderRadius: '6px', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <img src={isApiExpanded ? ARROW_DOWN_ICON_PATH : ARROW_RIGHT_ICON_PATH} alt={isApiExpanded ? "Collapse" : "Expand"} width="16" height="16" style={{ display: 'block' }} />
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Typography fontFamily="Gilroy-SemiBold" sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {api.name} </Typography>
                                                            <Chip label={api.gatewayCategory} size="small" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', bgcolor: '#F2F4F7', color: '#344054' }} />
                                                            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#F2F4F7', color: '#344054', p: '4px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                                        [{api.method}] /{api.endpoint}
                                                    </Typography>
                                                        </Box>
                                                        <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> 
                                                            {api.description} 
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, flexShrink: 0, ml: 2 }}>
                                                            
                                                            <Chip
                                                        label={api.isPublished ? "Live" : "Inactive"}
                                                        size="small"
                                                        sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', bgcolor: api.isPublished ? '#ECFDF3' : '#F2F4F7', color: api.isPublished ? '#027A48' : '#344054' }}
                                                    />
                                                            
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                // startIcon={<PageviewIcon />}
                                                                onClick={(e) => handleOpenSwaggerModal(api.swaggerTemplate, e)}
                                                                disabled={!api.swaggerTemplate}
                                                                sx={{
                                                                        backgroundColor: "black",
                                                                        fontFamily: 'Gilroy-Bold',
                                                                        color: "white",
                                                                        fontSize: '12px',
                                                                        borderRadius: '6px',
                                                                        textTransform: "none",
                                                                        py: 0.5,
                                                                        px: 1,
                                                                        mr: { xs: 0, md: 2 },
                                                                        '&:hover': {
                                                                            backgroundColor: '#333' // Darken button on hover
                                                                        },
                                                                        '&.Mui-disabled': {
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                                            color: 'rgba(0, 0, 0, 0.26)',
                                                                        }
                                                                    }}
                                                            >
                                                                View API Spec
                                                            </Button>
                                                            {/* --- NEW: Clone API Button --- */}
    <Button
        variant="contained"
        size="small"
        onClick={(e) => handleOpenCloneModal(api, e)}
        sx={{
            backgroundColor: "black",
            fontFamily: 'Gilroy-Bold',
            color: "white",
            fontSize: '12px',
            borderRadius: '6px',
            textTransform: "none",
            py: 0.5,
            px: 1,
            mr: { xs: 0, md: 2 },
            '&:hover': {
                backgroundColor: '#333'
            },
        }}
    >
        Clone API
    </Button>
                                                        {/* <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#F2F4F7', color: '#344054', p: '4px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                                                        [{api.method}] /{api.endpoint}
                                                    </Typography> */}
                                                    <Button variant="text" onClick={(e) => handleShowCredentials(e, api.gatewayName)}  sx={{ textTransform: 'none', fontFamily: 'Gilroy-SemiBold', color: "#333333", p: 0, '&:hover': { backgroundColor: 'transparent' } }}>
                                                        Show Credentials
                                                    </Button>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd', borderTop: '1px solid #eee' }}>
                                                <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                                                    {/* --- MODIFIED: Replaced Grid with Flexbox for alignment --- */}
                                                    <Box display="flex" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 3 }}>
                                                        {/* Info Group */}
                                                        <Box display="flex" alignItems="center" sx={{ gap: 4, flexWrap: 'wrap' }}>
                                                            <Box>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>Tech Stack</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{api.codeTemplate}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>Service Type</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{api.serviceType}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>Gateway</Typography>
                                                                <Typography variant="body2" component="div" sx={{ fontFamily: 'Gilroy-Medium' }}>{api.gatewayName}</Typography>
                                                            </Box>
                                                            {resourceUrls[api.gatewayName] && (
                            <Box>
                                <Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}> API Endpoint </Typography>
                                <Tooltip title={ <> <Typography fontFamily="Gilroy-Bold" fontSize="14px"> API Endpoint </Typography> {resourceUrls[api.gatewayName] && resourceUrls[api.gatewayName] !== "Loading..." ? ( <Link href={`${resourceUrls[api.gatewayName]}/${api.endpoint}`} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff', textDecoration: 'underline', wordBreak: 'break-all' }} > Endpoint </Link> ) : ( <Typography fontFamily="Gilroy-Regular" fontSize="12px" sx={{ color: '#ffffff' }}> Loading... </Typography> )} </> } slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }} >
                                    <IconButton sx={{ p: 0 }}>
                                        <Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                                                        </Box>
                                                        
                                                        {/* Button pushed to the right */}
                                                        {api.sequenceDiagram && (
                                                            <Box sx={{ ml: 'auto', alignSelf: 'flex-end' }}>
                                                                <Button 
                                                                    variant="text" 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenDiagramModal(api.sequenceDiagram || "");
                                                                    }}
                                                                    sx={{ 
                                                                        textTransform: 'none', 
                                                                        fontFamily: 'Gilroy-SemiBold', 
                                                                        color: "#333333", 
                                                                        p: 0, 
                                                                        '&:hover': { backgroundColor: 'transparent' } 
                                                                    }}
                                                                >
                                                                    Show Sequence Diagram
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>Input Schema</Typography>
                                                            <Paper component="pre" variant="outlined" sx={{ minHeight: '250px', maxHeight: '250px', overflowY: 'auto', p: 1.5, mt: 0.5, bgcolor: '#F9FAFB', borderRadius: '8px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                                <code>{JSON.stringify(api.inputSchema, null, 2)}</code>
                                                            </Paper>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0.5 }}>Output Schema</Typography>
                                                            <Paper component="pre" variant="outlined" sx={{ minHeight: '250px', maxHeight: '250px', overflowY: 'auto', p: 1.5, mt: 0.5, bgcolor: '#F9FAFB', borderRadius: '8px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                                <code>{JSON.stringify(api.outputSchema, null, 2)}</code>
                                                            </Paper>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            </Container>

            {/* Dialog for credentials (No changes) */}
            <Dialog open={isModalOpen} onClose={handleCloseModal} PaperProps={{ sx: { borderRadius: '12px', width: '100%', maxWidth: '500px', p: 1 }}}>
                <DialogTitle sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.25rem', p: 2, pb: 1 }}>
                    API Catalogue Credentials
                    <IconButton aria-label="close" onClick={handleCloseModal} sx={{ position: 'absolute', right: 2, top: 1.5, color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, pt: 1, minHeight: 150, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {isCredentialsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={24} />
                            <Typography sx={{ ml: 2, fontFamily: 'Gilroy-Regular' }}>Loading credentials...</Typography>
                        </Box>
                    ) : credentialsError ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                            <ErrorOutlineIcon color="error" sx={{ fontSize: 40, mb: 1 }}/>
                            {/* <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold', mb:1 }}>Failed to load data</Typography> */}
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>{credentialsError}</Typography>
                        </Box>
                    ) : credentials ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 2.5 }}>
                                Use the below Client ID, Client Secret and Client Key to authenticate your API requests.
                            </Typography>
                            <CredentialDisplayBox label="Client ID" value={credentials.clientId} />
                            <CredentialDisplayBox label="Client Secret" value={credentials.clientSecret} />
                            <CredentialDisplayBox label="Client key" value={credentials.clientKey} />
                        </>
                    ) : (
                         <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                            <ErrorOutlineIcon color="disabled" sx={{ fontSize: 40, mb: 1 }}/>
                            <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-SemiBold', mb:1 }}>Credentials not available</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>Please try again or contact support.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog 
                open={isDiagramModalOpen} 
                onClose={handleCloseDiagramModal} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.25rem', p: 2, pb: 1 }}>
                    Sequence Diagram
                    <IconButton 
                        aria-label="close" 
                        onClick={handleCloseDiagramModal} 
                        sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <MermaidDiagram chart={currentDiagram} />
                </DialogContent>
            </Dialog>

            {/* --- NEW: Dialog for Swagger UI --- */}
            <Dialog
                open={isSwaggerModalOpen}
                onClose={handleCloseSwaggerModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px', height: '90vh' } }}
            >
                <DialogTitle sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.25rem', p: 2, pb: 1 }}>
                    API Specification
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseSwaggerModal}
                        sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {swaggerSpec ? (
                        // ADD A WRAPPER BOX FOR CSS OVERRIDES
                        <Box sx={{
                            // This CSS hides the "Terms of Service", "Contact", and "License" links
                            '.swagger-ui .info a': {
                                display: 'none'
                            }
                        }}>
                            <SwaggerUI
                                spec={swaggerSpec}
                                // THIS PROP DISABLES THE "TRY IT OUT" BUTTONS
                                supportedSubmitMethods={[]} 
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography>No API specification available to display.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
            {/* --- NEW: Dialog for Cloning API --- */}
            <Dialog 
                open={isCloneModalOpen} 
                onClose={handleCloseCloneModal} 
                PaperProps={{ sx: { borderRadius: '12px', width: '100%', maxWidth: '500px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily: 'Gilroy-Bold', fontSize: '1.25rem', p: 2, pb: 1 }}>
                    Clone API: {apiToClone?.name}
                    <IconButton 
                        aria-label="close" 
                        onClick={handleCloseCloneModal} 
                        sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 2.5 }}>
                        Please enter a new name for the cloned API.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="New API Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newApiName}
                        onChange={(e) => setNewApiName(e.target.value)}
                        disabled={isCloning}
                    />
                    {cloneError && (
                        <Typography color="error" variant="body2" sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>
                            {cloneError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCloseCloneModal} sx={{ textTransform: 'none', fontFamily: 'Gilroy-SemiBold', color:'#232324' }}>Cancel</Button>
                    <Button 
                        onClick={handleCloneApi} 
                        variant="contained"
                        disabled={isCloning || !newApiName.trim() || isViewer}
                        sx={{ 
                            textTransform: 'none', 
                            fontFamily: 'Gilroy-Bold', 
                            backgroundColor: 'black', 
                            '&:hover': { backgroundColor: '#333' } 
                        }}
                    >
                        {isCloning ? <CircularProgress size={24} color="inherit" /> : 'Clone'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Main component with Drawer (No changes)
const CustomAPICatalogue: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"apis-catalogue"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <CustomAPICatalogueContent />
            </Box>
        </Box>
    );
};

export default CustomAPICatalogue;


